import { newCampaignsApi } from '@/store/newCampaignService';
import {
  STEP_SETUP,
  STEP_RECIPIENTS_MANUAL,
  STEP_REVIEW,
} from '@/pages/newSmsCampaigns/createCampaign/constants/steps';
import { campaignDoesNotBelongToCompany } from '@/pages/newSmsCampaigns/util/helpers';

// channel/type used by every audience + segment call in this flow
const CHANNEL = 'sms';
// channel_type expected by the file upload endpoint
const FILE_CHANNEL_TYPE = 'phone';

const getCollectionItems = (responseData = {}) => {
  const nestedData = responseData.data || {};

  return (Array.isArray(responseData) ? responseData : null)
    || responseData.items
    || responseData.segments
    || nestedData.items
    || nestedData.segments
    || (Array.isArray(responseData.data) ? responseData.data : [])
    || responseData.results
    || [];
};

const initializeForm = () => ({
  step: STEP_SETUP,
  isSaving: false,
  isLoading: false,
  data: null,
  recipientsType: null,
  // segments attached to the campaign (system segments, e.g. shareholders/contacts lists)
  attachedSegments: [],
  // draft audience items (manual / file) added via the /audience endpoints
  audienceItems: [],
});

const initializeCompanySettings = () => ({
  isLoading: false,
  data: null,
});

// system segments shown in the "Lists" tab of the recipients step
const initializeSystemSegments = () => ({
  isLoading: false,
  data: [],
});

const InitialState = {
  form: initializeForm(),
  companySettings: initializeCompanySettings(),
  systemSegments: initializeSystemSegments(),
};

const mutations = {
  // form mutations
  resetForm: (state) => Object.assign(state, {
    form: initializeForm(),
    systemSegments: initializeSystemSegments(),
  }),
  nextStep: (state) => {
    let nextStep;
    switch (state.form.step) {
      case STEP_SETUP:
        nextStep = STEP_RECIPIENTS_MANUAL;
        break;
      case STEP_RECIPIENTS_MANUAL:
        nextStep = STEP_REVIEW;
        break;
      default:
        nextStep = STEP_SETUP;
    }

    return Object.assign(state, {
      form: Object.assign(state.form, {
        step: nextStep,
      }),
    });
  },
  backStep: (state) => {
    let nextStep;
    switch (state.form.step) {
      case STEP_SETUP:
        nextStep = STEP_SETUP;
        break;
      case STEP_RECIPIENTS_MANUAL:
        nextStep = STEP_SETUP;
        break;
      case STEP_REVIEW:
        nextStep = STEP_RECIPIENTS_MANUAL;
        break;
      default:
        nextStep = STEP_SETUP;
    }

    return Object.assign(state, {
      form: Object.assign(state.form, {
        step: nextStep,
      }),
    });
  },
  gotoStep: (state, step) => Object.assign(state, {
    form: Object.assign(state.form, {
      step,
    }),
  }),
  setLoading: (state, isLoading) => Object.assign(state, {
    form: Object.assign(state.form, {
      isLoading,
    }),
  }),
  setSaving: (state, isSaving) => Object.assign(state, {
    form: Object.assign(state.form, {
      isSaving,
    }),
  }),
  setCampaignData: (state, data) => Object.assign(state, {
    form: Object.assign(state.form, {
      data,
    }),
  }),
  setAttachedSegments: (state, attachedSegments) => Object.assign(state, {
    form: Object.assign(state.form, {
      attachedSegments: attachedSegments || [],
    }),
  }),
  setAudienceItems: (state, audienceItems) => Object.assign(state, {
    form: Object.assign(state.form, {
      audienceItems: audienceItems || [],
    }),
  }),
  // company settings mutations
  setCompanySettingsLoading: (state, isLoading) => Object.assign(state, {
    companySettings: Object.assign(state.companySettings, {
      isLoading,
    }),
  }),
  setCompanySettingsData: (state, data) => Object.assign(state, {
    companySettings: Object.assign(state.companySettings, {
      data,
    }),
  }),
  // system segment (Lists tab) mutations
  setSystemSegmentsLoading: (state, isLoading) => Object.assign(state, {
    systemSegments: Object.assign(state.systemSegments, {
      isLoading,
    }),
  }),
  setSystemSegmentsData: (state, data) => Object.assign(state, {
    systemSegments: Object.assign(state.systemSegments, {
      data: data || [],
    }),
  }),
};

const actions = {
  async updateCampaign(context, { id, payload, enableSaving = true }) {
    try {
      if (enableSaving) context.commit('setSaving', true);

      await newCampaignsApi('put', `/api/v1/campaigns/${id}`, {
        data: payload,
      });
      // re-populate campaign data
      await context.dispatch('getCampaign', { id, enableLoading: false });
    } finally {
      if (enableSaving) context.commit('setSaving', false);
    }
  },
  async createCampaign(context, { payload, enableSaving = true }) {
    try {
      if (enableSaving) context.commit('setSaving', true);

      const res = await newCampaignsApi('post', '/api/v1/campaigns', {
        data: {
          ...payload,
          type: CHANNEL,
        },
      });

      const campaignId = res.data.id;

      await context.dispatch('getCampaign', {
        id: campaignId,
        enableLoading: false,
      });

      return campaignId;
    } finally {
      if (enableSaving) context.commit('setSaving', false);
    }
  },
  async forwardCampaign(context, payload) {
    try {
      context.commit('setSaving', true);

      const data = context.state.form.data;

      // create a brand new campaign cloned from the original's base fields
      return await context.dispatch('createCampaign', {
        enableSaving: false,
        payload: {
          name: data.name,
          content: data.content,
          // overrides from the setup step
          ...payload,
        },
      });
    } finally {
      context.commit('setSaving', false);
    }
  },
  async createCampaignWithRecipient(context, { campaignPayload, recipientPayload }) {
    try {
      context.commit('setSaving', true);

      // create a static segment from recipient data
      const segmentRes = await newCampaignsApi('post', '/api/v1/segments', {
        data: {
          ...recipientPayload,
          type: 'static',
          channel: CHANNEL,
        },
      });

      const segmentId = segmentRes.data.id;

      const fullCampaignPayload = {
        ...campaignPayload,
        segment_ids: [segmentId],
      };

      // create campaign and return campaign id
      const campaignId = await context.dispatch('createCampaign', {
        enableSaving: false,
        payload: fullCampaignPayload,
      });

      // attach segment to campaign
      await newCampaignsApi('post', `/api/v1/campaigns/${campaignId}/segments`, {
        data: { segment_ids: [segmentId] },
      });

      return campaignId;
    } finally {
      context.commit('setSaving', false);
    }
  },
  async getCampaign(context, { id, enableLoading = true }) {
    try {
      if (enableLoading) context.commit('setLoading', true);

      const res = await newCampaignsApi('get', `/api/v1/campaigns/${id}`);
      const data = res.data;

      context.commit('setCampaignData', data);

      // refresh draft audience items (manual / file). Never let this break the
      // campaign load — a non-draft campaign or transient failure just yields [].
      try {
        await context.dispatch('getAudienceItems', { id });
      } catch (error) {
        context.commit('setAudienceItems', []);
      }

      try {
        await context.dispatch('getCampaignSegments', { id });
      } catch (error) {
        context.commit('setAttachedSegments', data.segments || []);
      }
    } catch (error) {
      if (campaignDoesNotBelongToCompany(error)) {
        throw new Error('UnauthorizedCompany');
      } else {
        throw error;
      }
    } finally {
      if (enableLoading) context.commit('setLoading', false);
    }
  },
  // ----- Audience (draft) -----
  async getAudienceItems(context, { id }) {
    const res = await newCampaignsApi('get', `/api/v1/campaigns/${id}/audience`);
    const items = (res.data && res.data.items) || [];
    context.commit('setAudienceItems', items);
    return items;
  },
  // stateless live count used while editing a filter — creates nothing
  async previewAudience(context, { id, filter, channel = null }) {
    const res = await newCampaignsApi('post', `/api/v1/campaigns/${id}/audience/preview`, {
      data: { filter, channel },
    });
    return res.data;
  },
  async addFilterDraft(context, { label, filter }) {
    const id = context.state.form.data.id;

    const res = await newCampaignsApi('post', `/api/v1/campaigns/${id}/audience`, {
      data: {
        kind: 'filter',
        label,
        channel: CHANNEL,
        filter,
      },
    });

    await context.dispatch('getAudienceItems', { id });

    return res.data;
  },
  async addSegmentAudience(context, { label, segmentId }) {
    const id = context.state.form.data.id;

    const res = await newCampaignsApi('post', `/api/v1/campaigns/${id}/audience`, {
      data: {
        kind: 'segment',
        label,
        channel: CHANNEL,
        segment_id: segmentId,
      },
    });

    await context.dispatch('getAudienceItems', { id });

    return res.data;
  },
  // search existing contacts by type + name (cursor-paginated)
  async searchContacts(context, {
    contactType, q, limit = 50, cursor,
  } = {}) {
    const res = await newCampaignsApi('get', '/api/v1/contacts', {
      params: {
        contact_type: contactType,
        channel: CHANNEL,
        limit,
        ...(q ? { q } : {}),
        ...(cursor ? { cursor } : {}),
      },
    });
    return res.data;
  },
  // add already-existing contacts as a manual draft item
  async addContactsAsManual(context, { label, contactIds }) {
    const id = context.state.form.data.id;

    const res = await newCampaignsApi('post', `/api/v1/campaigns/${id}/audience`, {
      data: {
        kind: 'manual',
        label,
        channel: CHANNEL,
        contact_ids: contactIds,
      },
    });

    await context.dispatch('getAudienceItems', { id });

    return res.data;
  },
  async addManualContact(context, payload) {
    const id = context.state.form.data.id;

    // create the contact first (tagged as a manual contact), then reference it
    // as a manual draft item
    const contactRes = await newCampaignsApi('post', '/api/v1/contacts', {
      data: { ...payload, contact_type: 'manual' },
    });
    const contact = contactRes.data;
    const label = contact.phone
      || contact.mobile_number
      || payload.mobile_number
      || contact.name
      || 'Manual contact';

    const res = await newCampaignsApi('post', `/api/v1/campaigns/${id}/audience`, {
      data: {
        kind: 'manual',
        label,
        channel: CHANNEL,
        contact_ids: [contact.id],
      },
    });

    await context.dispatch('getAudienceItems', { id });

    return res.data;
  },
  async uploadAudienceFile(context, { file, label }) {
    const id = context.state.form.data.id;

    const formData = new FormData();
    formData.append('file', file);

    // returns { item, imported: { ok, updated, error }, rows: [...] }
    const res = await newCampaignsApi('post', `/api/v1/campaigns/${id}/audience/file`, {
      params: {
        channel_type: FILE_CHANNEL_TYPE,
        label,
      },
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      data: formData,
    }, 'data');

    await context.dispatch('getAudienceItems', { id });

    return res;
  },
  async removeAudienceItem(context, { itemId }) {
    const id = context.state.form.data.id;
    await newCampaignsApi('delete', `/api/v1/campaigns/${id}/audience/${itemId}`);
    await context.dispatch('getAudienceItems', { id });
  },
  // ----- Segments -----
  async getSegments(context, {
    contactType = 'shareholder',
    isSystem = true,
    channel = CHANNEL,
    limit = 50,
    q,
  } = {}) {
    const res = await newCampaignsApi('get', '/api/v1/segments', {
      params: {
        contact_type: contactType,
        is_system: isSystem,
        type: 'dynamic',
        channel,
        limit,
        ...(q ? { q } : {}),
      },
    });

    return (res.data && res.data.items) || (Array.isArray(res.data) ? res.data : []);
  },
  async getSystemSegments(context, { q } = {}) {
    try {
      context.commit('setSystemSegmentsLoading', true);

      const data = await context.dispatch('getSegments', {
        contactType: 'shareholder',
        isSystem: true,
        channel: CHANNEL,
        q,
      });
      context.commit('setSystemSegmentsData', data);
      return data;
    } finally {
      context.commit('setSystemSegmentsLoading', false);
    }
  },
  async getCampaignSegments(context, { id } = {}) {
    const campaignId = id || (context.state.form.data && context.state.form.data.id);

    if (!campaignId) {
      context.commit('setAttachedSegments', []);
      return [];
    }

    const res = await newCampaignsApi('get', `/api/v1/campaigns/${campaignId}/segments`);
    const items = getCollectionItems(res.data);
    context.commit('setAttachedSegments', items);

    return items;
  },
  async attachSegments(context, { segmentIds }) {
    const id = context.state.form.data.id;
    await newCampaignsApi('post', `/api/v1/campaigns/${id}/segments`, {
      data: { segment_ids: segmentIds },
    });
    await context.dispatch('getCampaignSegments', { id });
  },
  async detachSegment(context, { segmentId }) {
    const id = context.state.form.data.id;
    await newCampaignsApi('delete', `/api/v1/campaigns/${id}/segments/${segmentId}`);
    await context.dispatch('getCampaignSegments', { id });
  },
  async getCompanySettings(context, forceRefresh = false) {
    try {
      const companyId = context.rootGetters['company/company_id'];

      // exit function if we have already fetched settings for current company
      if (
        context.state.companySettings.data
        && (
          context.state.companySettings.data.company_id === companyId
          || context.state.companySettings.data.id === companyId
        )
        && !forceRefresh
      ) return;

      context.commit('setCompanySettingsLoading', true);

      const res = await newCampaignsApi('get', '/api/v1/companies', {
        params: { ids: companyId },
      });

      const list = res.data.items || res.data;
      const data = Array.isArray(list) ? list[0] : list;

      context.commit('setCompanySettingsData', data ? {
        ...data,
        company_id: data.company_id || data.id || companyId,
      } : null);
    } finally {
      context.commit('setCompanySettingsLoading', false);
    }
  },
  async updateCompanySettings(context, payload) {
    const companyId = context.rootGetters['company/company_id'];
    await newCampaignsApi('put', `/api/v1/companies/${companyId}`, {
      data: payload,
    });
    // refresh company data
    await context.dispatch('getCompanySettings', true);
  },
  async scheduleCampaign(context, { id, payload }) {
    try {
      context.commit('setSaving', true);

      await newCampaignsApi('post', `/api/v1/campaigns/${id}/schedule`, {
        data: payload,
      });
      await context.dispatch('getCampaign', { id, enableLoading: false });
    } finally {
      context.commit('setSaving', false);
    }
  },
  async sendCampaign(context, { id }) {
    try {
      context.commit('setSaving', true);

      await newCampaignsApi('post', `/api/v1/campaigns/${id}/send`, {
        data: {},
      });
      await context.dispatch('getCampaign', { id, enableLoading: false });
    } finally {
      context.commit('setSaving', false);
    }
  },
  async pauseCampaign(context, { id }) {
    try {
      context.commit('setSaving', true);

      await newCampaignsApi('post', `/api/v1/campaigns/${id}/pause`, {
        data: {},
      });
      await context.dispatch('getCampaign', { id, enableLoading: false });
    } finally {
      context.commit('setSaving', false);
    }
  },
  async resumeCampaign(context, { id }) {
    try {
      context.commit('setSaving', true);

      await newCampaignsApi('post', `/api/v1/campaigns/${id}/resume`, {
        data: {},
      });
      await context.dispatch('getCampaign', { id, enableLoading: false });
    } finally {
      context.commit('setSaving', false);
    }
  },
  async stopCampaign(context, { id }) {
    try {
      context.commit('setSaving', true);

      await newCampaignsApi('post', `/api/v1/campaigns/${id}/stop`, {
        data: {},
      });
      await context.dispatch('getCampaign', { id, enableLoading: false });
    } finally {
      context.commit('setSaving', false);
    }
  },
};

const getSegmentId = (segment) => (
  segment.segment_id
  || segment.id
  || segment.uid
  || (segment.segment && segment.segment.id)
);

const getSegmentName = (segment) => (
  segment.name
  || segment.label
  || (segment.segment && (segment.segment.name || segment.segment.label))
  || 'Unnamed list'
);

const getSegmentCount = (segment) => (
  segment.member_count
  || segment.contact_count
  || segment.count
  || (segment.segment && (
    segment.segment.member_count
    || segment.segment.contact_count
    || segment.segment.count
  ))
  || 0
);

// normalize an attached segment into the unified recipient-group shape
const normalizeSegment = (segment) => ({
  uid: getSegmentId(segment),
  itemType: 'segment',
  source: 'list',
  name: getSegmentName(segment),
  count: getSegmentCount(segment),
  count_unique: getSegmentCount(segment),
  status: segment.status || null,
  segment_id: getSegmentId(segment),
});

// normalize a draft audience item into the unified recipient-group shape
const normalizeAudienceItem = (item) => {
  const count = item.count != null ? item.count : (item.row_count || 0);
  const source = item.kind || item.type || 'manual';
  const normalizedSource = source === 'segment' ? 'list' : source;

  return {
    uid: item.id,
    itemType: 'draft',
    source: normalizedSource,
    name: item.label,
    count,
    count_unique: count,
    status: item.status || 'pending',
    segment_id: item.segment_id || null,
  };
};

const getters = {
  // unified list combining attached segments + draft audience items
  recipientGroups: (state) => [
    ...state.form.attachedSegments.map(normalizeSegment),
    ...state.form.audienceItems.map(normalizeAudienceItem),
  ],
  listRecipients: (state, sgetters) => sgetters.recipientGroups
    .filter((item) => item.itemType === 'segment' || item.source === 'list'),
  fileRecipients: (state, sgetters) => sgetters.recipientGroups
    .filter((item) => item.source === 'file'),
  filterRecipients: (state, sgetters) => sgetters.recipientGroups
    .filter((item) => item.source === 'filter'),
  manualRecipients: (state, sgetters) => sgetters.recipientGroups
    .filter((item) => item.source === 'manual'),
  totalRecipientCount: (state, sgetters) => sgetters.recipientGroups
    .reduce((acc, item) => acc + (item.count || 0), 0),
  // send/schedule preflight passes with >=1 attached segment OR >=1 draft item
  hasRecipients: (state, sgetters) => sgetters.recipientGroups.length > 0,
  // audience controls (add/file/delete) are allowed while the campaign is a
  // draft or still scheduled (not yet processing/sent)
  canEditAudience: (state) => !!state.form.data
    && (state.form.data.status === 'draft' || state.form.data.status === 'scheduled'),
  // "Save as draft" only applies to a brand-new (not yet persisted) campaign or
  // one that is still a draft. Once it's scheduled/sent it's no longer a draft,
  // so the action is hidden.
  canSaveDraft: (state) => !state.form.data || state.form.data.status === 'draft',
  campaignsFeatureIsSetup: (state) => state.companySettings
    && state.companySettings.data
    && state.companySettings.data.allow_sms_campaigns,
  campaignsFeatureIsActive: (state) => state.companySettings
    && state.companySettings.data
    && state.companySettings.data.client_status
    && state.companySettings.data.client_status === 'active',
  // ids of system segments already attached to the campaign (for the Lists tab)
  attachedSegmentIdMap: (state) => state.form.attachedSegments.reduce((acc, item) => {
    const id = getSegmentId(item);
    if (id) acc[id] = true;
    return acc;
  }, state.form.audienceItems.reduce((acc, item) => {
    const source = item.kind || item.type;
    if ((source === 'segment' || source === 'list') && item.segment_id) {
      acc[item.segment_id] = true;
    }
    return acc;
  }, {})),
};

export default {
  state: InitialState,
  mutations,
  actions,
  getters,
  namespaced: true,
};
